<template>
  <div>
    <loading :active.sync="isLoading" :is-full-page="fullPageLoader" :color="loaderColor"></loading>
    <div class="flex flex-col" style="max-width: 575px; margin: 0 auto">
      <div class="content-start mt-3">
        <div v-if="fileAdded">
          <div class="flex items-center">
            <gd-checkbox v-model="containsMarkdown" variant="primary" class="float-left"
              :label="$t('frontend_grooveblog_import_markdown_checkbox')"
              labelClass="ml-3 text-secondary-dark font-bold text-sm" />
          </div>
          <div class="my-4 bg-white rounded shadow">
            <div class="flex flex-col border border-grey-500 rounded-t">
              <div class="flex justify-between p-3">
                <div class="w-5/12">
                  <p class="text-md text-dark uppercase font-semibold">{{
                    this.$t('frontend_grooveblog_import_multiple_posts_header') }}</p>
                </div>
                <div class="w-7/12">
                  <p class="text-md text-dark uppercase font-semibold">{{
                    this.$t('frontend_grooveblog_import_multiple_posts_field') }}</p>
                </div>
              </div>
            </div>

            <div v-for="(header, index) in importedPostHeaders" :key="index"
              class="py-2 border-b border-r border-l border-grey-500"
              :class="{ 'mb-2 rounded-b': index === importedPostHeaders - 1 }">
              <div class="flex items-center justify-between px-3">
                <div class="w-5/12">
                  <p>{{ header.name }}</p>
                </div>
                <div class="w-6/12 flex items-center">
                  <div class="flex flex-col">
                    <treeselect class="h-10 custom-treeselect import-treeselect" :multiple="false" v-model="header.key"
                      :options="fieldsData" :closeOnSelect="closeOnSelect" :clearable="clearable"
                      :clearOnSelect="clearOnSelect" :maxHeight="210" @input="fieldForColumnSelected()" :placeholder="$t('frontend_grooveblog_import_select_field')
                        " />
                    <div v-if="header.error" class="text-sm text-primary-dark">
                      {{ $t('frontend_grooveblog_import_field_required') }}
                    </div>
                  </div>
                </div>
                <div class="w-1/12 flex justify-center items-center">
                  <div :title="$t('frontend_grooveblog_import_unmap_header_title')" class="cursor-pointer"
                    @click="removeKeyForHeader(index)" v-if="header.key !== null &&
                      header.key !== undefined
                      ">
                    <i class="fas fa-undo ml-2 text-grey-700"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col mt-2">
        <gd-button class="w-full mb-6 px-10 h-10" v-if="fileAdded" variant="primary" :disabled="disableImport"
          @click="importBlogPosts">{{
            $t('frontend_grooveblog_import_new_post')
          }}</gd-button>
        <div class="flex mb-2">
          <span class="w-1/2">
            <form enctype="multipart/form-data">
              <label class="upload-leads-btn h-10 btn-width px-4 btn-radius text-base font-semibold focus:outline-none shadow hover:shadow-lg
                focus:shadow-lg rounded-lg focus:text-black  focus:border-black border border-solid "
                for="upload-leads" :style="blogsButtonViaImport">
                {{
                  $t(
                    fileAdded
                      ? 'frontend_grooveblog_import_select_different_file'
                      : 'frontend_grooveblog_import_select_file'
                  )
                }}
              </label>
              <input id="upload-leads" type="file" accept=".csv" ref="fileUpload" @click="resetUploader"
                @change="onFileChange" />
            </form>
          </span>

          <gd-button class="w-1/2 h-10 btn-width px-4 btn-radius text-base font-semibold ml-2" size="auto"
            @click="downloadSample()">
            <span>{{ $t('frontend_grooveblog_import_sample_file')
            }}<span class="fa fa-download ml-2" aria-hidden="true"></span></span>
          </gd-button>
        </div>
        <p v-if="fileRequired && (!uploadFiles || !uploadFiles.length)" class="text-sm text-primary-dark mt-2">
          {{
            this.$t(
              'frontend_grooveblog_import_new_multiple_select_file_post_to_import'
            )
          }}
        </p>
        <p v-if="fileError" class="text-sm text-primary-dark mt-2">
          {{ fileError }}
        </p>
        <p class="text-sm text-grey-700 font-semibold mb-4">
          <span class="fa fa-info-circle mr-1"></span>
          <span>{{
            $t('frontend_grooveblog_import_posts_only_csv_allowed')
          }}</span>
        </p>
      </div>

      <portal to="headerContent">
        <gd-button size="auto" class="px-10 h-10 ml-2" variant="primary" :disabled="disableImport"
          @click="importBlogPosts">{{
            $t('frontend_grooveblog_import_button')
          }}</gd-button>
      </portal>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

import Swal from 'sweetalert2';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import themeConfig from '@/theme/themeConfig.js';

export default {
  metaInfo() {
    return {
      title: this.$t('frontend_grooveblog_import_new_post')
    };
  },
  components: {
    Loading,
    Treeselect,
  },
  data() {
    return {
      isLoading: false,
      fullPageLoader: true,
      loaderColor: themeConfig.appPrimaryColor,
      fileRequired: false,
      fileError: '',
      fileId: null,
      uploadFiles: null,
      importedPostHeaders: [],
      clearable: false,
      closeOnSelect: true,
      clearOnSelect: true,
      containsMarkdown: false,
      disableImport: false,
    };
  },

  computed: {
    blogsButtonViaImport() {
      const css = {
        '--import-button-bg-color': this.fileAdded ? 'var(--brand-primary-invert)' : 'var(--brand-primary)',
        '--import-button-text-color': !this.fileAdded ? 'var(--brand-primary-invert)' : 'var(--brand-primary-dark)',
        '--import-button-hover-color': this.fileAdded ? 'var(--brand-primary-invert)' : 'var(--brand-primary-dark)',
      };

      if (!this.fileAdded) css.border = 0;

      return css;
    },
    fieldsData() {
      let options = [
        { id: 0, label: 'Title' },
        { id: 1, label: 'Post Content' },
        { id: 2, label: 'Slug' },
        { id: 3, label: 'Post Image' },
        { id: 4, label: 'Publish Time' },
        { id: 5, label: 'Excerpt' },
        { id: 6, label: 'Add To Carousel (True/False)' },
        { id: 7, label: 'Show Sidebar (True/False)' },
        { id: 8, label: 'Show On Homepage (True/False)' },
        { id: 9, label: 'Allow Comments (True/False)' },
        { id: 10, label: 'Popular Post (True/False)' },
        { id: 11, label: 'Status (Draft/Published)' },
        { id: 12, label: 'Categories' },
        { id: 13, label: 'Keyword tags' },
        { id: 14, label: 'Meta Title' },
        { id: 15, label: 'Meta Description' },
        { id: 16, label: 'Meta Keywords' },
        { id: 17, label: 'Open Graph Title' },
        { id: 18, label: 'Open Graph Image URL' },
        { id: 19, label: 'Open Graph Description' },
      ];

      const selected = this.importedPostHeaders.map((el) => {
        if (el.key !== null) {
          return el.key;
        }
      });
      options = options.map((el) => {
        el.isDisabled = selected.includes(el.id);
        return el;
      });
      return options;
    },

    fileAdded() {
      return this.importedPostHeaders && this.importedPostHeaders.length;
    }
  },

  methods: {
    downloadSample() {
      const downloadLink = `${window.gdEnv.VUE_APP_API_URL}/grooveblog/blogpost/dummy_blog_post`;
      window.open(downloadLink, '_self');
    },

    async importBlogPosts() {
      if (!(this.uploadFiles && this.uploadFiles.length)) {
        this.fileRequired = true;
        return;
      }

      this.showLoader();
      const titleSelected = this.importedPostHeaders.find((el) => el.key === 0);
      if (typeof titleSelected === 'undefined') {
        Swal.fire(
          this.$t('frontend_grooveblog_import_blogpost_title_not_selected'),
          '',
          'error'
        );
        this.hideLoader();
        return;
      }

      if (!this.fileRequired) {
        const selectionOrder = this.fieldsData.map((el) => {
          if (el.isDisabled) {
            return this.importedPostHeaders.findIndex((element) => element.key === el.id);
          }
          return null;
        });

        try {
          const response = await axios.post('/blogpost/import', {
            fileId: this.fileId,
            contains_markdown: this.containsMarkdown,
            selectionOrder,
          });

          if (response) {
            if (response.data.status) {
              this.disableImport = true;
              this.$parent.$parent.$refs.importMsg.toggleShow(true);
            } else {
              Swal.fire(
                response.data.message,
                '',
                'error'
              );
            }
          }
        } catch (error) {
          console.error(error);
        }
      }
      this.hideLoader();
    },
    resetUploader() {
      this.$refs.fileUpload.value = '';
    },
    async onFileChange(e) {
      this.fileRequired = false;
      this.uploadFiles = e.target.files || e.dataTransfer.files;

      try {
        this.showLoader();
        const validateRes = await axios.post('/import-blog/upload-posts-csv', this.getFileValidateDataPayload());
        this.importedPostHeaders = [];
        if (
          typeof validateRes.data.data !== 'undefined' &&
          validateRes.data.data &&
          validateRes.status === 201
        ) {
          this.fileId = validateRes.data.data.id;
          this.fileError = '';
          this.$papa.parse(this.uploadFiles[0], {
            header: false,
            skipEmptyLines: 'greedy',
            complete: (results) => {
              this.hideLoader();
              results.data[0].forEach((ele) => {
                this.importedPostHeaders.push({
                  name: ele,
                  key: null
                });
              });
            }
          });

          const main = document.getElementsByTagName('main').item(0).classList;
          if (Object.values(main).includes('overflow-y-auto')) {
            main.remove('overflow-y-auto');
          }
        } else if (validateRes.data.status === 400) {
          this.fileId = null;
          this.fileError = validateRes.data.message;
        } else if (validateRes.data.status === false) {
          Swal.fire(
            this.$t('frontend_error'),
            validateRes.data.message,
            'error'
          );
        }
      } catch (err) {
        console.error(err);
      }
      this.hideLoader();
    },
    getFileValidateDataPayload() {
      const dataForFileValidate = new FormData();
      dataForFileValidate.append('blogId', this.$store.getters.getBlogId);
      dataForFileValidate.append('csv', this.uploadFiles[0]);
      dataForFileValidate.append('csvName', this.uploadFiles[0].name);
      return dataForFileValidate;
    },
    fieldForColumnSelected() {
      this.importedPostHeaders = this.importedPostHeaders.map((el) => {
        if (typeof el.error !== 'undefined') {
          el.error = el.key === null;
        }
        return el;
      });
    },
    removeKeyForHeader(index) {
      this.importedPostHeaders[index].key = null;
    },
    showLoader() {
      if (!this.isLoading) {
        this.isLoading = true;
      }
    },
    hideLoader() {
      if (this.isLoading) {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style>
.import-treeselect.vue-treeselect--single .vue-treeselect__control .vue-treeselect__single-value {
  color: #333 !important;
}
</style>

<style lang="scss" scoped>
.content-start:empty {
  display: block;

  &:after {
    content: 'Import blog posts in bulk by selecting your CSV file or by creating one using the provided sample file.';
  }
}

.content-start:empty:before {
  content: "Import your Blog Posts";
  background-repeat: no-repeat;
  background-size: 205px 138px;
  background-position: top;
  height: 200px;
  font-size: 16px;
  font-weight: 700;
  padding-top: 170px;
  color: #191d3a;
}
</style>

<style lang="scss">
#upload-leads {
  opacity: 0;
  position: absolute;
  z-index: -1;
}

.upload-leads-btn {
  margin: 0px !important;
  display: block;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  position: relative;
  width: auto;
  cursor: pointer;
  height: 40px;
  border-radius: 6px;
  outline: 0;
  background: var(--import-button-bg-color);
  color: var(--import-button-text-color);
  font-size: 16px;
  text-align: center;
  line-height: 40px;
  font-weight: 600 !important;
}

.upload-leads-btn:hover {
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.upload-leads-btn:hover {
  background: var(--import-button-hover-color);
}

.upload-leads-btn:after {
  background: var(--brand-primary);
  color: var(--brand-primary-invert);
  text-align: center;
  position: absolute;
  top: 0;
  left: 0;
  font-weight: 600 !important;
  width: auto;
  display: block;
}
</style>
